(() => {
  if (customElements.get('mofu-delivery-checker')) return;
  const PIN = /^[1-9][0-9]{5}$/;
  const storageKey = 'mofu-delivery-pincode';
  const checkers = new Set();
  let pendingCoverage;
  let pendingUrl;
  let selectedPin = '';
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    const age = saved && Date.now() - saved.savedAt;
    if (saved && PIN.test(saved.pin) && Number.isFinite(age) && age >= 0 && age < 30 * 60 * 1000) selectedPin = String(saved.pin);
  } catch (_) { /* Private browsing may disable storage; checking still works. */ }

  function validateCoverage(coverage) {
    const expires = Date.parse(coverage?.expires + 'T00:00:00+05:30');
    if (coverage?.version !== 1 || !coverage.pins || !Array.isArray(coverage.places) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(coverage.updated) || !/^\d{4}-\d{2}-\d{2}$/.test(coverage.expires) ||
        !Number.isFinite(expires) || Date.now() >= expires) throw new Error('Coverage needs refreshing');
    return coverage;
  }

  function loadCoverage(url) {
    // Recheck freshness on every lookup, including when a tab has been open for a long time.
    if (pendingCoverage && pendingUrl === url) return pendingCoverage.then(validateCoverage);
    pendingUrl = url;
    const request = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Coverage unavailable');
        return validateCoverage(await response.json());
      } finally { clearTimeout(timeout); }
    })();
    pendingCoverage = request;
    return request.catch(error => {
      if (pendingCoverage === request) pendingCoverage = undefined;
      throw error;
    });
  }

  class MofuDeliveryChecker extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector('[data-pincode]')) {
        queueMicrotask(() => { if (this.isConnected && this.querySelector('[data-pincode]')) this.connectedCallback(); });
        return;
      }
      if (!this.initialized) {
        this.initialized = true;
        this.revision = 0;
        this.draft = selectedPin;
        this.report = '';
        this.state = '';
        this.noteText = '';
        this.busy = false;
        this.invalid = false;
        // Delegate on the host because Shopify can replace the input and button during a cart render.
        this.addEventListener('click', event => {
          if (event.target.closest('[data-check-pincode]')) this.check();
        });
        this.addEventListener('keydown', event => {
          if (event.key === 'Enter' && event.target.matches('[data-pincode]')) {
            event.preventDefault(); event.stopPropagation(); this.check();
          }
        });
        this.addEventListener('input', event => {
          if (!event.target.matches('[data-pincode]')) return;
          this.revision++;
          this.draft = event.target.value;
          this.report = '';
          this.state = '';
          this.invalid = false;
          this.busy = false;
          this.render();
        });
        this.observer = new MutationObserver(() => this.render());
      }
      checkers.add(this);
      if (selectedPin && this.draft !== selectedPin) {
        this.draft = selectedPin; this.report = ''; this.invalid = false; this.state = '';
      }
      this.observer.observe(this, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
      this.render();
      if (this.draft && !this.report) this.check(false);
    }

    disconnectedCallback() {
      checkers.delete(this);
      this.observer?.disconnect();
      this.revision++;
      if (this.busy) { this.busy = false; this.report = ''; this.state = ''; }
    }

    adoptPin(pin) {
      this.draft = pin;
      this.render();
      this.check(false);
    }

    render() {
      this.input = this.querySelector('[data-pincode]');
      this.button = this.querySelector('[data-check-pincode]');
      this.result = this.querySelector('[data-pincode-result]');
      this.note = this.querySelector('[data-coverage-note]');
      if (!this.input || !this.button || !this.result) return;
      if (this.input.value !== this.draft) this.input.value = this.draft;
      if (this.invalid) {
        if (this.input.getAttribute('aria-invalid') !== 'true') this.input.setAttribute('aria-invalid', 'true');
      } else if (this.input.hasAttribute('aria-invalid')) this.input.removeAttribute('aria-invalid');
      this.button.disabled = this.busy;
      const label = this.busy ? 'Checking…' : 'Check';
      if (this.button.textContent !== label) this.button.textContent = label;
      if (this.result.textContent !== this.report) this.result.textContent = this.report;
      if (this.result.hidden !== !this.report) this.result.hidden = !this.report;
      if (this.state) this.dataset.state = this.state;
      else delete this.dataset.state;
      if (this.noteText && this.note && this.note.textContent !== this.noteText) this.note.textContent = this.noteText;
    }

    message(state, text) {
      this.state = state;
      this.report = text;
      this.render();
    }

    async check(synchronize = true) {
      const revision = ++this.revision;
      const pin = this.input.value.trim();
      this.draft = pin;
      if (!PIN.test(pin)) {
        this.invalid = true;
        this.busy = false;
        this.message('invalid', 'Please enter a valid 6-digit Indian pincode.');
        this.input.focus();
        return;
      }
      if (synchronize) {
        selectedPin = pin;
        try { sessionStorage.setItem(storageKey, JSON.stringify({ pin, savedAt: Date.now() })); } catch (_) {}
        for (const checker of checkers) {
          if (checker !== this && checker.isConnected) checker.adoptPin(pin);
        }
      }
      this.invalid = false;
      this.busy = true;
      this.message('loading', 'Checking delivery coverage…');
      try {
        const data = await loadCoverage(this.dataset.coverageUrl);
        if (!this.isConnected || revision !== this.revision) return;
        const date = new Date(data.updated + 'T00:00:00+05:30').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
        this.noteText = `Delhivery coverage list updated ${date}. Final delivery and payment options are confirmed at checkout.`;
        const entry = Object.hasOwn(data.pins, pin) ? data.pins[pin] : null;
        const flags = entry?.[1];
        const location = entry && data.places[entry[0]];
        if (entry && (!Array.isArray(location) || !Number.isInteger(flags) || flags < 0 || flags > 3)) throw new Error('Invalid coverage entry');
        if (!entry || flags === 0) {
          this.message('unknown', `We can’t confirm Delhivery delivery to ${pin} from the current coverage list. Please try another pincode or contact us before ordering.`);
        } else {
          const area = location.filter(Boolean).join(', ');
          const options = [];
          if (flags & 1) options.push('Prepaid delivery available');
          if (flags & 2) options.push('COD coverage available — checkout eligibility applies');
          else options.push('Prepaid only — COD is not listed for this pincode');
          this.message('available', `Delivery coverage found for ${pin}${area ? ` (${area})` : ''}.\n${options.join('. ')}.`);
        }
      } catch (_) {
        if (this.isConnected && revision === this.revision) {
          this.message('unknown', 'We can’t check delivery right now. Please try again later or contact support@mofulabs.com before ordering.');
        }
      } finally {
        if (this.isConnected && revision === this.revision) { this.busy = false; this.render(); }
      }
    }
  }
  customElements.define('mofu-delivery-checker', MofuDeliveryChecker);
})();
